import type { ISecaoFicha } from '../../../../hooks/useEventoContrato';

/** O texto da seção como a tela edita: um parágrafo por linha. */
export const textoDaSecao = (s: ISecaoFicha): string => s.paragrafos.map(p => p.texto).join('\n');

/** O que vai para o PUT da ficha: as linhas com texto, cada uma um parágrafo. */
export const paragrafosDoTexto = (texto: string): string[] =>
  texto
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

/** Cada seção tem o seu "papel" na tela (resumo, dock, ir para o campo) — não um só para todas. */
export const PREFIXO_SECAO = 'secao-';
export const papelDaSecao = (chave: string): string => `${PREFIXO_SECAO}${chave}`;

/**
 * O texto da tela diz o mesmo que o servidor tem. A tela guarda o Enter e o espaço que o
 * salvamento normaliza: comparar o texto cru fazia a recarga trocar o que o operador estava
 * digitando pela versão salva (a linha nova sumia e o cursor ia para o fim).
 */
export const mesmoTexto = (texto: string, s: ISecaoFicha): boolean =>
  JSON.stringify(paragrafosDoTexto(texto)) ===
  JSON.stringify(s.paragrafos.map(p => p.texto.trim()).filter(Boolean));

export const secaoVazia = (texto: string): boolean => paragrafosDoTexto(texto).length === 0;

/** Editada e ainda diferente do servidor: depois de salva, vale o estado que o servidor deu. */
export const editadaNaTela = (editadas: Record<string, string>, s: ISecaoFicha): boolean =>
  s.chave in editadas && !mesmoTexto(editadas[s.chave], s);

export interface IEstadoSecao {
  cor: string;
  texto: string;
}

/** Como a seção está: sem texto e número sem fonte barram o contrato; a redigida pela IA pede conferência. */
export function estadoDaSecao(s: ISecaoFicha, editado: boolean): IEstadoSecao {
  if (editado) return { cor: 'green', texto: 'editada — salva sozinha' };
  if (s.removida) return { cor: 'gray', texto: 'não está na proposta — sai do contrato' };
  if (!s.paragrafos.length) return { cor: 'red', texto: 'não redigida' };
  if (s.conferido === false) return { cor: 'red', texto: 'número sem fonte' };
  if (s.fonte === 'operador') return { cor: 'green', texto: 'você escreveu' };
  return { cor: 'orange', texto: 'redigida pela IA — confira' };
}

/**
 * As seções editadas no formato do PUT — só as que mudaram de verdade. Mexer e voltar ao mesmo
 * texto não pode virar "você escreveu": no servidor isso tirava o bloqueio de número sem fonte.
 */
export const secoesParaSalvar = (
  editadas: Record<string, string>,
  secoes: ISecaoFicha[],
): Record<string, string[]> =>
  Object.fromEntries(
    Object.entries(editadas)
      .filter(([chave, texto]) => {
        const s = secoes.find(x => x.chave === chave);
        return !!s && !mesmoTexto(texto, s);
      })
      .map(([chave, texto]) => [chave, paragrafosDoTexto(texto)]),
  );
